import { get } from './api';
import type {
  StockData,
  ChartDataPoint,
  ProcessedFinancialMetricsPayload,
  NewsEvent
} from '../types/game';
import type { StockCategoryFilter } from '../types/stockCategories';

export interface DailyStockSelection {
  stock: StockData;
  chartData: ChartDataPoint[];
  processedFinancialMetrics: ProcessedFinancialMetricsPayload | null;
  newsEvents: NewsEvent[] | null;
  source: 'api';
}

const logPrefix = '[categoryStockSelection]';

const validateChartPoint = (point: any): point is ChartDataPoint => {
  return (
    point &&
    typeof point === 'object' &&
    typeof point.date === 'string' &&
    typeof point.price === 'number' &&
    Number.isFinite(point.price)
  );
};

export const validateChartData = (chartData: unknown): chartData is ChartDataPoint[] => {
  if (!Array.isArray(chartData)) {
    console.warn(`${logPrefix} Chart data is not an array`, { type: typeof chartData });
    return false;
  }

  if (chartData.length === 0) {
    console.warn(`${logPrefix} Chart data array is empty`);
    return false;
  }

  const sample = chartData.slice(0, Math.min(chartData.length, 3));
  const invalidPoints = sample.filter((point) => !validateChartPoint(point));

  if (invalidPoints.length > 0) {
    console.warn(`${logPrefix} Invalid chart data points detected`, { invalidPoints });
    return false;
  }

  return true;
};

export const processChartData = (
  chartData: unknown,
  stock?: StockData
): ChartDataPoint[] => {
  if (validateChartData(chartData)) {
    return chartData;
  }

  if (stock?.chart_data && validateChartData(stock.chart_data)) {
    return stock.chart_data;
  }

  console.warn(`${logPrefix} No valid chart data available`, {
    hasChartDataField: !!chartData,
    hasStockChartData: !!stock?.chart_data
  });

  return [];
};

const parseDailyStockResponse = (raw: any): DailyStockSelection => {
  const payload = raw?.data ?? raw;

  if (!payload) {
    throw new Error('Missing stock payload');
  }

  const stock: StockData = payload.stock ?? payload;
  if (!stock || typeof stock !== 'object') {
    throw new Error('Invalid stock payload');
  }

  const chartData = processChartData(payload.chart_data, stock);
  const processedFinancialMetrics =
    stock.processed_financial_metrics ??
    (payload as { processed_financial_metrics?: ProcessedFinancialMetricsPayload | null })?.processed_financial_metrics ??
    null;
  const newsEvents: NewsEvent[] | null =
    Array.isArray(payload.news_events) ? payload.news_events : null;

  return {
    stock,
    chartData,
    processedFinancialMetrics,
    newsEvents,
    source: 'api'
  };
};

export const getDailyStockForCategory = async (
  category: StockCategoryFilter,
  date?: string
): Promise<DailyStockSelection> => {
  try {
    const endpoint = date
      ? `/api/${category}/daily-stock?date=${date}`
      : `/api/${category}/daily-stock`;
    const response = await get(endpoint);
    return parseDailyStockResponse(response);
  } catch (error) {
    console.error(`${logPrefix} Failed to fetch daily stock`, {
      category,
      date: date || 'today',
      error
    });
    throw error;
  }
};

export const debugChartData = async (category: StockCategoryFilter): Promise<void> => {
  try {
    const result = await getDailyStockForCategory(category);
    console.log(`${logPrefix} Debug chart data`, {
      category,
      source: result.source,
      symbol: result.stock?.Symbol,
      chartDataLength: result.chartData.length,
      chartDataSample: result.chartData.slice(0, 3)
    });
  } catch (error) {
    console.error(`${logPrefix} Debug chart data failed`, { category, error });
  }
};

export const CategoryStockUtils = {
  getDailyStockForCategory,
  processChartData,
  validateChartData,
  debugChartData
};

export default CategoryStockUtils;
